import { BrandDetails } from '../store/reducers/brandSlice';

// Domain-keyed so multi-tenant portals on different subdomains each get their own cache.
const BRAND_CACHE_KEY = `brand_cache_${window.location.hostname}`;

/**
 * Reads brand details synchronously from localStorage.
 * Returns null if nothing is cached or if the stored value is malformed.
 */
export const getBrandFromCache = (): BrandDetails | null => {
    try {
        const raw = localStorage.getItem(BRAND_CACHE_KEY);
        if (!raw) return null;
        return JSON.parse(raw) as BrandDetails;
    } catch {
        return null;
    }
};

/**
 * Persists brand details to localStorage after a successful API fetch.
 * Silently ignores storage errors (quota exceeded, private browsing restrictions).
 */
export const saveBrandToCache = (brand: BrandDetails): void => {
    try {
        localStorage.setItem(BRAND_CACHE_KEY, JSON.stringify(brand));
    } catch {
        // Storage unavailable — fail silently, app still works without cache.
    }
};

/**
 * Removes the cached brand for this domain.
 * Not called on logout because brand is public/portal-level data (not user-specific).
 */
export const clearBrandCache = (): void => {
    try {
        localStorage.removeItem(BRAND_CACHE_KEY);
    } catch {
        // Ignore.
    }
};

// Mirrors Typography.ts fontOptions so we can resolve a font index to a name
// without importing MUI (which would load the full bundle before React mounts).
const FONT_OPTIONS = ['Inter', 'Roboto', 'Open Sans', 'Lato', 'Montserrat', 'Poppins'];
const DEFAULT_FONT = 'Inter';

const resolveFontFamily = (font: string | number | null | undefined): string => {
    if (!font && font !== 0) return DEFAULT_FONT;
    const idx = typeof font === 'number' ? font : parseInt(String(font), 10);
    if (!isNaN(idx) && idx >= 1 && idx <= FONT_OPTIONS.length) return FONT_OPTIONS[idx - 1];
    const name = String(font).trim();
    return name || DEFAULT_FONT;
};

/**
 * Injects a <style> block that sets body font-family from the cached brand synchronously,
 * BEFORE React mounts. Combined with font-display:block in index.html this eliminates
 * the font-swap flash: the browser knows the correct font-family on first paint and
 * never needs to repaint with a different family.
 *
 * Call this in index.tsx before root.render().
 */
export const applyFontFromCache = (): void => {
    try {
        const brand = getBrandFromCache();
        const fontFamily = resolveFontFamily(brand?.Font);
        const style = document.createElement('style');
        style.setAttribute('data-brand-font', 'true');
        style.textContent = `body, #root { font-family: '${fontFamily}', sans-serif; }`;
        document.head.appendChild(style);
    } catch {
        // Ignore — font application is cosmetic, must never break app startup.
    }
};

/**
 * Applies the cached brand favicon synchronously — call this BEFORE React mounts
 * (i.e. in index.tsx before root.render) so the browser never shows the spinner
 * SVG from index.html for returning visitors.
 *
 * On first-ever load (no cache) this is a no-op; the spinner remains until
 * App.tsx useEffect resolves the API response and calls applyFaviconUrl().
 */
export const applyFaviconFromCache = (): void => {
    try {
        const brand = getBrandFromCache();
        if (!brand?.FavLogo) return;

        const favLogoPath = brand.FavLogo;
        const profileUrlBase = process.env.REACT_APP_PROFILE_URL || '';
        const faviconUrl =
            favLogoPath.startsWith('http://') || favLogoPath.startsWith('https://')
                ? favLogoPath
                : `${profileUrlBase}${favLogoPath}`;

        const setHref = (selector: string) => {
            const el = document.querySelector(selector) as HTMLLinkElement | null;
            if (el) el.href = faviconUrl;
        };

        setHref("link[rel='icon']");
        setHref("link[rel='apple-touch-icon']");
    } catch {
        // Ignore — favicon is cosmetic, must never break app startup.
    }
};
